import type { Camera } from "./camera.js";

/**
 * Input handler - wires mouse, touch, keyboard, and wheel events
 * to the Camera. Manages event listeners and cleanup.
 */
export class InputHandler {
  private camera: Camera;
  private view: HTMLCanvasElement;
  private enabled = true;

  // Touch state
  private lastTouchDist = 0;
  private lastTouchCX = 0;
  private lastTouchCY = 0;

  // Bound handlers (for removal)
  private _onWheel: (e: WheelEvent) => void;
  private _onMouseDown: (e: MouseEvent) => void;
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseUp: (e: MouseEvent) => void;
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onTouchStart: (e: TouchEvent) => void;
  private _onTouchMove: (e: TouchEvent) => void;
  private _onTouchEnd: (e: TouchEvent) => void;

  /** Called when the user clicks (not drags) the map. Receives screen coords. */
  onClick: ((sx: number, sy: number) => void) | null = null;

  /** Whether the pointer moved beyond the deadzone between the last down and up. */
  get wasDragMove(): boolean {
    return this._dragMoved;
  }
  private _dragMoved = false;
  private _downX = 0;
  private _downY = 0;
  // Small-movement tolerance (px) so that sub-pixel jitter during a
  // tap/click doesn't accidentally count as a drag and suppress marker clicks.
  private static readonly DRAG_DEADZONE = 4;

  constructor(camera: Camera, view: HTMLCanvasElement) {
    this.camera = camera;
    this.view = view;

    // Make canvas focusable for keyboard events
    view.tabIndex = 0;
    view.style.outline = "none";

    this._onWheel = this.handleWheel.bind(this);
    this._onMouseDown = this.handleMouseDown.bind(this);
    this._onMouseMove = this.handleMouseMove.bind(this);
    this._onMouseUp = this.handleMouseUp.bind(this);
    this._onKeyDown = this.handleKeyDown.bind(this);
    this._onTouchStart = this.handleTouchStart.bind(this);
    this._onTouchMove = this.handleTouchMove.bind(this);
    this._onTouchEnd = this.handleTouchEnd.bind(this);

    view.addEventListener("wheel", this._onWheel, { passive: false });
    view.addEventListener("mousedown", this._onMouseDown);
    window.addEventListener("mousemove", this._onMouseMove);
    window.addEventListener("mouseup", this._onMouseUp);
    view.addEventListener("keydown", this._onKeyDown);
    view.addEventListener("touchstart", this._onTouchStart, { passive: true });
    view.addEventListener("touchmove", this._onTouchMove, { passive: false });
    view.addEventListener("touchend", this._onTouchEnd, { passive: true });
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  private handleWheel(e: WheelEvent): void {
    if (!this.enabled) return;
    e.preventDefault();

    // Normalize delta to pixels across browsers/devices
    let delta = e.deltaY;
    if (e.deltaMode === 1) delta *= 16; // line mode
    if (e.deltaMode === 2) delta *= 100; // page mode

    const rect = this.view.getBoundingClientRect();
    this.camera.zoomAt(e.clientX - rect.left, e.clientY - rect.top, delta);
  }

  private handleMouseDown(e: MouseEvent): void {
    if (!this.enabled) return;
    this.camera.startDrag(e.clientX, e.clientY);
    this.view.style.cursor = "grabbing";
    this._dragMoved = false;
    this._downX = e.clientX;
    this._downY = e.clientY;
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.enabled) return;
    // Only flag as a drag once the pointer exceeds the deadzone, so
    // tiny movements during a click don't suppress markerclick events.
    if (this.camera.isDragging && !this._dragMoved) {
      const dx = e.clientX - this._downX;
      const dy = e.clientY - this._downY;
      if (dx * dx + dy * dy > InputHandler.DRAG_DEADZONE * InputHandler.DRAG_DEADZONE) {
        this._dragMoved = true;
      }
    }
    this.camera.drag(e.clientX, e.clientY);
  }

  private handleMouseUp(e: MouseEvent): void {
    if (!this.enabled) return;
    const wasDragging = this.camera.isDragging;
    this.camera.endDrag();
    this.view.style.cursor = "grab";

    // If we mousedown'd + mouseup'd without dragging, it's a click
    if (wasDragging && !this._dragMoved && this.onClick) {
      const rect = this.view.getBoundingClientRect();
      this.onClick(e.clientX - rect.left, e.clientY - rect.top);
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.enabled) return;
    const panStep = 60;
    const cam = this.camera;
    const rect = this.view.getBoundingClientRect();

    switch (e.key) {
      case "ArrowLeft":
        cam.targetX += panStep;
        cam.vx = 0;
        break;
      case "ArrowRight":
        cam.targetX -= panStep;
        cam.vx = 0;
        break;
      case "ArrowUp":
        cam.targetY += panStep;
        cam.vy = 0;
        break;
      case "ArrowDown":
        cam.targetY -= panStep;
        cam.vy = 0;
        break;
      case "+":
      case "=":
        cam.zoomAt(rect.width / 2, rect.height / 2, -100);
        break;
      case "-":
        cam.zoomAt(rect.width / 2, rect.height / 2, 100);
        break;
      case "Home":
      case "0":
        cam.fitToView(rect.width, rect.height);
        break;
      default:
        return; // Don't prevent default for unhandled keys
    }
    e.preventDefault();
  }

  private handleTouchStart(e: TouchEvent): void {
    if (!this.enabled) return;
    if (e.touches.length === 1) {
      this._dragMoved = false;
      this._downX = e.touches[0].clientX;
      this._downY = e.touches[0].clientY;
      this.camera.startDrag(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      this.camera.isDragging = false;
      this.camera.vx = 0;
      this.camera.vy = 0;
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      this.lastTouchDist = Math.hypot(
        t1.clientX - t0.clientX,
        t1.clientY - t0.clientY,
      );
      const rect = this.view.getBoundingClientRect();
      this.lastTouchCX = (t0.clientX + t1.clientX) / 2 - rect.left;
      this.lastTouchCY = (t0.clientY + t1.clientY) / 2 - rect.top;
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.enabled) return;
    e.preventDefault();
    if (e.touches.length === 1 && this.camera.isDragging) {
      // Mirror the mouse deadzone check for touch so that the drag guard
      // (wasDragMove) works on touch devices — without this, touch-drags
      // never set the flag and markerclick fires even after a swipe.
      if (!this._dragMoved) {
        const dx = e.touches[0].clientX - this._downX;
        const dy = e.touches[0].clientY - this._downY;
        if (dx * dx + dy * dy > InputHandler.DRAG_DEADZONE * InputHandler.DRAG_DEADZONE) {
          this._dragMoved = true;
        }
      }
      this.camera.drag(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
      const rect = this.view.getBoundingClientRect();
      const cx = (t0.clientX + t1.clientX) / 2 - rect.left;
      const cy = (t0.clientY + t1.clientY) / 2 - rect.top;

      // Two-finger pan
      this.camera.panBy(cx - this.lastTouchCX, cy - this.lastTouchCY);

      // Pinch zoom centered on midpoint
      if (this.lastTouchDist > 0) {
        const ratio = dist / this.lastTouchDist;
        this.camera.pinchZoomAt(cx, cy, this.camera.scale * ratio);
      }

      this.lastTouchDist = dist;
      this.lastTouchCX = cx;
      this.lastTouchCY = cy;
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (e.touches.length < 2) {
      this.lastTouchDist = 0;
    }
    if (e.touches.length === 0) {
      this.camera.endDrag();
    }
  }

  destroy(): void {
    this.view.removeEventListener("wheel", this._onWheel);
    this.view.removeEventListener("mousedown", this._onMouseDown);
    window.removeEventListener("mousemove", this._onMouseMove);
    window.removeEventListener("mouseup", this._onMouseUp);
    this.view.removeEventListener("keydown", this._onKeyDown);
    this.view.removeEventListener("touchstart", this._onTouchStart);
    this.view.removeEventListener("touchmove", this._onTouchMove);
    this.view.removeEventListener("touchend", this._onTouchEnd);
  }
}
